export type TextCaret = { index: number }

const BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE'])

function isBlock(el: Element) {
  return BLOCK_TAGS.has(el.tagName)
}

// Tæller “tekstlængde” med virtuelle \n:
// - tekstnodes længde
// - <br> = 1
// - efter block elements (fx </li>) = 1
function countWithSeparators(node: Node): number {
  let n = 0

  const walk = (cur: Node) => {
    if (cur.nodeType === Node.TEXT_NODE) {
      n += cur.nodeValue?.length ?? 0
      return
    }
    if (cur.nodeType === Node.ELEMENT_NODE) {
      const el = cur as Element
      if (el.tagName === 'BR') {
        n += 1
        return
      }
      // børn
      for (let child = cur.firstChild; child; child = child.nextSibling) {
        walk(child)
      }
      // separator efter block
      if (isBlock(el)) n += 1
      return
    }

    // andre node-typer ignoreres
  }

  walk(node)
  return n
}

function getSelectionForRoot(root: HTMLElement, jodit?: any): Selection | null {
  // hvis Jodit kører i iframe, har den typisk editorWindow/editorDocument
  const win = jodit?.editorWindow ?? root.ownerDocument.defaultView
  return win?.getSelection?.() ?? null
}

export function getCaretIndex(root: HTMLElement): TextCaret | null {
  const sel = root.ownerDocument.defaultView?.getSelection?.()
  if (!sel || sel.rangeCount === 0) return null

  const r = sel.getRangeAt(0)
  if (!root.contains(r.startContainer)) return null

  const pre = root.ownerDocument.createRange()
  pre.selectNodeContents(root)
  pre.setEnd(r.startContainer, r.startOffset)

  // Text længde
  const textLen = pre.toString().length

  // Separatorer (li/br) baseret på HTML i fragmentet
  const div = root.ownerDocument.createElement('div')
  div.appendChild(pre.cloneContents())
  const html = div.innerHTML.toLowerCase()

  const brCount = (html.match(/<br\b/g) ?? []).length
  const liEndCount = (html.match(/<\/li>/g) ?? []).length

  return { index: textLen + brCount + liEndCount }
}

export function restoreCaretIndex(root: HTMLElement, caret: TextCaret): boolean {
  const doc = root.ownerDocument
  const sel = doc.defaultView?.getSelection?.()
  if (!sel) return false

  let remaining = caret.index

  const setRangeAt = (node: Node, offset: number) => {
    const range = doc.createRange()
    range.setStart(node, offset)
    range.collapse(true)
    sel.removeAllRanges()
    sel.addRange(range)
    return true
  }

  // DFS-walk der tæller:
  // - text nodes: længde
  // - <br>: +1
  // - efter hvert </li>: +1 (det håndterer vi ved "exit" af LI)
  const walk = (node: Node): boolean => {
    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.nodeValue?.length ?? 0
      if (remaining <= len) return setRangeAt(node, remaining)
      remaining -= len
      return false
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element

      if (el.tagName === 'BR') {
        if (remaining <= 1) {
          const range = doc.createRange()
          range.setStartAfter(el)
          range.collapse(true)
          sel.removeAllRanges()
          sel.addRange(range)
          return true
        }
        remaining -= 1
        return false
      }

      // børn
      for (let child = node.firstChild; child; child = child.nextSibling) {
        if (walk(child)) return true
      }

      // "exit" LI => +1
      if (el.tagName === 'LI') {
        if (remaining <= 1) {
          // land ved slutningen af LI (det matcher vores </li>-count)
          const range = doc.createRange()
          range.selectNodeContents(el)
          range.collapse(false)
          sel.removeAllRanges()
          sel.addRange(range)
          return true
        }
        remaining -= 1
      }

      return false
    }

    return false
  }

  if (walk(root)) return true

  // fallback: slut
  const range = doc.createRange()
  range.selectNodeContents(root)
  range.collapse(false)
  sel.removeAllRanges()
  sel.addRange(range)
  return true
}

export function getCaretIndexWithSeparators(root: HTMLElement, jodit?: any): { index: number } | null {
  const sel = getSelectionForRoot(root, jodit)
  console.log(`getCaretIndexWithSeparators 1`)
  if (!sel || sel.rangeCount === 0) return null
  console.log(`getCaretIndexWithSeparators 2`)

  const r = sel.getRangeAt(0)

  // VIGTIGT: caret skal faktisk være inde i root
  const startNode = r.startContainer
  if (!root.contains(startNode)) {
    console.log(`getCaretIndexWithSeparators 3`)
    // Typisk tegn på iframe/wrong root
    return { index: 0 }
  }

  console.log(`getCaretIndexWithSeparators 4`, startNode)

  const doc = root.ownerDocument
  const pre = doc.createRange()
  pre.selectNodeContents(root)
  pre.setEnd(r.startContainer, r.startOffset)

  const frag = pre.cloneContents()
  return { index: countWithSeparators(frag) }
}

// Gå DOM i samme “måleregler” og find index
export function restoreCaretIndexWithSeparators(root: HTMLElement, caret: TextCaret): boolean {
  let remaining = caret.index

  const placeAtEndOfElement = (el: Element) => {
    // find sidste position inde i elementet
    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(false)
    const sel = window.getSelection()
    if (!sel) return false
    sel.removeAllRanges()
    sel.addRange(range)
    return true
  }

  const walk = (cur: Node): boolean => {
    if (cur.nodeType === Node.TEXT_NODE) {
      const text = cur as Text
      const len = text.nodeValue?.length ?? 0
      if (remaining <= len) {
        const range = document.createRange()
        range.setStart(text, remaining)
        range.collapse(true)
        const sel = window.getSelection()
        if (!sel) return false
        sel.removeAllRanges()
        sel.addRange(range)
        return true
      }
      remaining -= len
      return false
    }

    if (cur.nodeType === Node.ELEMENT_NODE) {
      const el = cur as Element

      if (el.tagName === 'BR') {
        // <br> tæller som 1
        if (remaining <= 1) {
          // hvis remaining == 0: før br, hvis ==1: efter br.
          const range = document.createRange()
          range.setStartAfter(el)
          range.collapse(true)
          const sel = window.getSelection()
          if (!sel) return false
          sel.removeAllRanges()
          sel.addRange(range)
          return true
        }
        remaining -= 1
        return false
      }

      for (let child = cur.firstChild; child; child = child.nextSibling) {
        if (walk(child)) return true
      }

      // separator efter block element (fx </li>)
      if (isBlock(el)) {
        if (remaining <= 1) {
          // land “ved enden” af blokken
          return placeAtEndOfElement(el)
        }
        remaining -= 1
      }

      return false
    }

    return false
  }

  // hvis vi ikke kan finde præcist, så land til sidst
  return walk(root) || placeAtEndOfElement(root)
}
